/**
 * Shared whitelabel-branding query hook — replaces 9 near-identical
 * useState+useEffect fetches of POST /agency/getwhitelabel that were
 * duplicated across the auth pages.
 *
 * `getwhitelabel` resolves the agency by domain via the same
 * `AgencyModel.findOne`/`findById` the GET /agency/?_id= endpoint uses, so
 * its `data` is already the full agency document (Customization, Categories,
 * stripe, etc.) — no follow-up GET /agency/?_id= lookup needed.
 *
 * For the single per-app-load fetch, prefer reading `agency` off
 * UsersContext (context/usersContext.js) instead of calling this hook again
 * — it already runs this query once at the app root.
 */
import { useMutation, useQuery } from "@tanstack/react-query";
import { request, ReqMethods } from "../lib/apiClient";

const { GET, POST, PUT } = ReqMethods;

export const agencyKeys = {
  all: ["agency"],
  whitelabel: (domain, token) => [...agencyKeys.all, "whitelabel", domain, token],
};

/**
 * @param {string} [domain] - defaults to the current hostname, matching every
 *   call site's `new URL(window.location.href).hostname` derivation.
 * @param {string} [token] - forwarded in the payload for the set-password
 *   flow, whose endpoint also validates the token and returns `token_status`.
 */
export function useWhitelabelAgency({ domain = window.location.hostname, token } = {}) {
  const whitelabelQuery = useQuery({
    queryKey: agencyKeys.whitelabel(domain, token),
    queryFn: () =>
      request(POST, "/agency/getwhitelabel", {
        payload: token ? { domain, token } : { domain },
      }),
  });

  return {
    whitelabel: whitelabelQuery.data?.data,
    tokenStatus: whitelabelQuery.data?.token_status,
    isLoading: whitelabelQuery.isLoading,
    isSuccess: whitelabelQuery.isSuccess,
    error: whitelabelQuery.error,
  };
}

// SSOIframe's getAgency — GET /agency/?company_id=X, fired from a useEffect
// reacting to decoded SSO data, not rendered off a cache key.
export function useAgencyByCompanyId() {
  return useMutation({
    mutationFn: (companyId) => request(GET, "/agency/", { query: `company_id=${companyId}` }),
  });
}

// SSOIframe's updateAgency.
export function useUpdateAgencyByCompanyId() {
  return useMutation({
    mutationFn: (payload) => request(PUT, "/agency/update_by_company_id", { payload }),
  });
}
